import {MediaCardType} from '@wandb/weave/common/types/media';
import React, {useMemo} from 'react';

import {CustomRunNames} from '../../state/views/customRunNames/types';
import * as PanelTypes from '../../state/views/panel/types';
import {PartRefFromObjSchema} from '../../state/views/types';
import {getMediaMetadata} from '../../util/media';
import {getCustomRunName} from '../../util/runs';
import {TileMediaSpec} from '../MediaCard';
import {Axis, NonEmptyTile} from './common';
import {getGroupedMediaDimensions} from './dimensions';
import {MediaBrowserPanelTileTrackingContextProvider} from './MediaBrowserPanelTileTrackingContext';
import {TileMediaLoader} from './TileMediaLoader';
import {TileWithMaskOptions} from './tiles';

type PanelTileProps = {
  tile: TileWithMaskOptions & NonEmptyTile;
  tileIndex: number;
  cardWidth: number;
  cardHeight: number;
  config: any;
  mediaType: MediaCardType;
  mediaSliderKey: string;
  labels: Axis[];
  panelRef: PartRefFromObjSchema<PanelTypes.PanelObjSchema>;
  disableRunLinks?: boolean;
  customRunNames?: CustomRunNames;
};

// PanelTile component for individual tile rendering with proper memoization
const PanelTile = React.memo<PanelTileProps>((props: PanelTileProps) => {
  const {
    tile,
    tileIndex,
    cardWidth,
    cardHeight,
    config,
    mediaType,
    mediaSliderKey,
    labels,
    panelRef,
    disableRunLinks,
    customRunNames,
  } = props;
  const {run, mediaIndex, mediaKey, mediaStep, runSignature, maskOptions} =
    tile;
  const tileMediaSpec: TileMediaSpec = useMemo(
    () => ({
      mediaType,
      run,
      mediaIndex,
      mediaKey,
      mediaStep,
      mediaSliderKey,
      runSignature,
      pixelated: config.pixelated,
    }),
    [
      mediaType,
      run,
      mediaIndex,
      mediaKey,
      mediaStep,
      mediaSliderKey,
      runSignature,
      config.pixelated,
    ]
  );

  const runNotes = run.notes;
  const customRunName = getCustomRunName(run.name, false, customRunNames);

  const tileAndPanelControls = useMemo(
    () => ({
      ...tile.controls,
      segmentationMaskControl: config.segmentationMaskConfig,
      boundingBoxControl: config.boundingBoxConfig,
    }),
    [tile.controls, config.segmentationMaskConfig, config.boundingBoxConfig]
  );

  const metadata = getMediaMetadata(tile.run.history, mediaKey, mediaStep);
  const groupedDimensions = getGroupedMediaDimensions(metadata);

  // Create a stable key for the maskKeys to prevent unnecessary remounts
  const stableMaskKey = [...maskOptions.maskKeys].sort().join(',');
  const componentKey = `card-${tileIndex}-${mediaKey}-${stableMaskKey}`;

  // Memoize the props object to prevent unnecessary re-renders
  const tileMediaLoaderProps = useMemo(
    () => ({
      tileMediaSpec,
      attemptNonexistentSteps: !config.snapToExistingStep,
      run,
      runSignature,
      runNotes,
      customRunName,
      labels,
      globalStep: mediaStep,
      mediaKey,
      mediaIndex,
      width: cardWidth,
      height: cardHeight,
      mediaWidth: groupedDimensions?.width,
      mediaHeight: groupedDimensions?.height,
      actualSize: !!config.actualSize,
      controls: tileAndPanelControls,
      mediaPanelRef: panelRef,
      moleculeConfig: config.moleculeConfig,
      disableRunLink: disableRunLinks,
      maskOptions,
      config,
    }),
    [
      tileMediaSpec,
      run,
      runSignature,
      runNotes,
      customRunName,
      labels,
      mediaStep,
      mediaKey,
      mediaIndex,
      cardWidth,
      cardHeight,
      groupedDimensions?.width,
      groupedDimensions?.height,
      tileAndPanelControls,
      panelRef,
      disableRunLinks,
      maskOptions,
      config,
    ]
  );

  return (
    <MediaBrowserPanelTileTrackingContextProvider
      key={componentKey}
      mediaKey={mediaKey}
      panelId={panelRef.id}
      runName={run.name}>
      <TileMediaLoader key={componentKey} {...tileMediaLoaderProps} />
    </MediaBrowserPanelTileTrackingContextProvider>
  );
});

export default PanelTile;
